import React, { useState, useRef, useEffect } from "react";
import api, { apiError } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { MessageCircle, X, Send, Loader2, Leaf } from "lucide-react";

const HELLO = {
  bg: "Здравейте! Аз съм дигиталният консиерж на Върбица. С какво мога да помогна — стая, ресторант или резервация?",
  en: "Hello! I'm the Varbitsa digital concierge. How can I help — a room, the restaurant, or a booking?",
  de: "Hallo! Ich bin der digitale Concierge von Varbitsa. Wie kann ich helfen — Zimmer, Restaurant oder Buchung?",
  fr: "Bonjour ! Je suis le concierge digital de Varbitsa. Comment puis-je vous aider — chambre, restaurant ou réservation ?",
};

export default function ConciergeWidget() {
  const { lang } = useLang();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const sessionRef = useRef(localStorage.getItem("vb_concierge") || `c-${Date.now()}-${Math.random().toString(36).slice(2)}`);
  const scrollRef = useRef(null);

  useEffect(() => { localStorage.setItem("vb_concierge", sessionRef.current); }, []);
  useEffect(() => {
    if (open && messages.length === 0) setMessages([{ role: "assistant", content: HELLO[lang] || HELLO.bg }]);
  }, [open]); // eslint-disable-line
  useEffect(() => { scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight); }, [messages, busy]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setMessages((m) => [...m, { role: "user", content: text }]);
    setInput("");
    setBusy(true);
    try {
      const { data } = await api.post("/ai/concierge", { session_id: sessionRef.current, message: text });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: apiError(e) }]);
    } finally { setBusy(false); }
  };

  return (
    <>
      <button onClick={() => setOpen(!open)} data-testid="concierge-toggle"
        className="fixed bottom-6 right-6 z-[60] w-16 h-16 rounded-full bg-forest text-gold shadow-card flex items-center justify-center hover:scale-105 transition-transform">
        {open ? <X className="w-7 h-7" /> : <MessageCircle className="w-7 h-7" />}
      </button>

      {open && (
        <div className="fixed bottom-24 right-6 z-[60] w-[92vw] max-w-sm h-[70vh] max-h-[560px] bg-white rounded-2xl shadow-card border border-gold/20 flex flex-col overflow-hidden animate-fade-up" data-testid="concierge-panel">
          <div className="bg-forest-deep text-cream p-4 flex items-center gap-2">
            <Leaf className="w-5 h-5 text-gold" />
            <div>
              <div className="font-display text-lg leading-none">Консиерж Върбица</div>
              <div className="text-[10px] text-gold-light uppercase tracking-widest">AI асистент</div>
            </div>
          </div>
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-3 bg-cream">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] px-3 py-2 rounded-2xl text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-forest text-white rounded-br-sm" : "bg-white border border-gold/15 text-forest-deep rounded-bl-sm"}`}
                  data-testid={`concierge-msg-${m.role}`}>
                  {m.content}
                </div>
              </div>
            ))}
            {busy && <div className="flex justify-start"><div className="bg-white border border-gold/15 px-3 py-2 rounded-2xl"><Loader2 className="w-4 h-4 animate-spin text-gold-dark" /></div></div>}
          </div>
          <div className="p-3 border-t border-gold/10 flex items-center gap-2 bg-white">
            <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Напишете съобщение…" data-testid="concierge-input"
              className="flex-1 h-11 px-3 rounded-xl border border-gold/20 bg-cream text-sm outline-none focus:border-gold" />
            <button onClick={send} disabled={busy} data-testid="concierge-send"
              className="w-11 h-11 rounded-xl bg-gold text-forest-deep flex items-center justify-center disabled:opacity-50">
              <Send className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
